import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Check as CheckIcon,
    ContentCopy as CopyIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    PersonAdd as SignupIcon,
    Phone as PhoneIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import {loadNonUserEmployees, NonUserEmployee, signup, SignupRequest, SignupResponse} from '../../services/signup.service';
import {ResponseStatus} from '../../models/api-response.model';
import {useAuth} from '../../contexts/auth.context';

function Signup() {
    const [formData, setFormData] = useState<{
        employeeId: number | null;
        password: string;
        confirmPassword: string;
    }>({
        employeeId: null,
        password: '',
        confirmPassword: ''
    });

    const [employees, setEmployees] = useState<NonUserEmployee[]>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [signupResponse, setSignupResponse] = useState<SignupResponse | null>(null);
    const [copySuccess, setCopySuccess] = useState<{ email: boolean, phone: boolean }>({email: false, phone: false});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const {isAuthenticated} = useAuth();

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home', {replace: true});
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        loadNonUserEmployees().then(response => {
            if (response.status === ResponseStatus.SUCCESS) {
                setEmployees(response.body);
            }
        });
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleEmployeeChange = (_event: React.SyntheticEvent, value: NonUserEmployee | null) => {
        if (!value?.id) {
            return;
        }
        setFormData(prev => ({
            ...prev,
            employeeId: value.id
        }));

        // Clear error when user makes a selection
        if (errors.employeeId) {
            setErrors(prev => ({...prev, employeeId: ''}));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.employeeId) {
            newErrors.employeeId = 'Please select an employee';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCopy = async (text: string, type: 'email' | 'phone') => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(prev => ({...prev, [type]: true}));

            // Reset copy success icon after 2 seconds
            setTimeout(() => {
                setCopySuccess(prev => ({...prev, [type]: false}));
            }, 2000);

            setSnackbarMessage(`${type === 'email' ? 'Email' : 'Phone number'} copied to clipboard!`);
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');
        setSignupResponse(null);

        try {
            const request: SignupRequest = {
                employeeId: String(formData.employeeId!),
                password: formData.password
            };

            const response = await signup(request);

            setIsLoading(false);

            if (response.status === ResponseStatus.SUCCESS) {
                setSuccess('Account created successfully!');
                setSignupResponse(response.body);
            } else {
                setError(response.message || 'Registration failed. Please try again.');
            }
        } catch (error) {
            setIsLoading(false);
            setError('An error occurred during registration. Please try again.');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    sx={{
                        maxWidth: 500,
                        mx: 'auto',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    }}
                >
                    <CardContent sx={{p: 4}}>
                        <Box sx={{textAlign: 'center', mb: 4}}>
                            <SignupIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'primary.main',
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                                Create Account
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Register for a new account
                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handleSubmit}
                             sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>

                            <Autocomplete
                                id="employeeId"
                                options={employees}
                                getOptionLabel={(option) => {
                                    return `${option.firstName} ${option.middleName ? option.middleName + ' ' : ''} ${option.lastName}`;
                                }}
                                renderOption={(props, option) => {
                                    const {key, ...otherProps} = props;
                                    return (
                                        <li key={key} {...otherProps}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.firstName} {option.middleName ? option.middleName + ' ' : ''}{option.lastName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email: {option.email} | Phone: {option.phone}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Your Employee Profile"
                                        placeholder="Search your name"
                                        error={!!errors.employeeId}
                                        helperText={errors.employeeId || "Find and select your name from the employee list"}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '&:hover': {
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <PersonIcon sx={{color: 'text.secondary', mr: 1, ml: 1}}/>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {isLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                onChange={handleEmployeeChange}
                                loading={isLoading}
                                loadingText="Loading employees..."
                                noOptionsText="No employee profiles found"
                                fullWidth
                            />

                            <TextField
                                fullWidth
                                id="password"
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                variant="outlined"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Create a strong password"
                                required
                                error={!!errors.password}
                                helperText={errors.password || 'Create a secure password with at least 6 characters'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <LockIcon sx={{color: 'text.secondary', mr: 1, ml: 1}}/>
                                    ),
                                    endAdornment: (
                                        <IconButton
                                            onClick={togglePasswordVisibility}
                                            edge="end"
                                            sx={{mr: 1}}
                                            aria-label="toggle password visibility"
                                        >
                                            {showPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                        </IconButton>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                id="confirmPassword"
                                name="confirmPassword"
                                label="Confirm Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                variant="outlined"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Re-enter your password"
                                required
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword || 'Re-enter your password to confirm it matches'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <LockIcon sx={{color: 'text.secondary', mr: 1, ml: 1}}/>
                                    ),
                                    endAdornment: (
                                        <IconButton
                                            onClick={toggleConfirmPasswordVisibility}
                                            edge="end"
                                            sx={{mr: 1}}
                                            aria-label="toggle confirm password visibility"
                                        >
                                            {showConfirmPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                        </IconButton>
                                    )
                                }}
                            />

                            {error && (
                                <Alert severity="error" sx={{mb: 2, borderRadius: 2}}>
                                    {error}
                                </Alert>
                            )}

                            {success && signupResponse && (
                                <Paper elevation={3}
                                       sx={{p: 3, mb: 3, borderRadius: 2, position: 'relative', overflow: 'hidden'}}>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '4px',
                                        bgcolor: 'success.main'
                                    }}/>

                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                        <CheckIcon sx={{color: 'success.main', mr: 1, fontSize: 28}}/>
                                        <Typography variant="h6" color="success.main">
                                            {success}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{my: 2}}/>

                                    <Typography variant="body1" sx={{mb: 2, fontWeight: 'medium'}}>
                                        You can log in using either of these credentials:
                                    </Typography>

                                    <Stack spacing={2} sx={{mb: 2}}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: 'background.paper',
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <EmailIcon sx={{mr: 2, color: 'primary.main'}}/>
                                            <Box sx={{flexGrow: 1}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email Address
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {signupResponse.email}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Copy email">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(signupResponse.email, 'email')}
                                                    color="primary"
                                                >
                                                    {copySuccess.email ? <CheckIcon/> : <CopyIcon/>}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: 'background.paper',
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <PhoneIcon sx={{mr: 2, color: 'primary.main'}}/>
                                            <Box sx={{flexGrow: 1}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Phone Number
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {signupResponse.phone}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Copy phone number">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(signupResponse.phone, 'phone')}
                                                    color="primary"
                                                >
                                                    {copySuccess.phone ? <CheckIcon/> : <CopyIcon/>}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Stack>

                                    <Box sx={{
                                        mt: 3,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            component={Link}
                                            to="/login"
                                            sx={{flexGrow: 1}}>
                                            Go to Login
                                        </Button>
                                    </Box>
                                </Paper>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} color="inherit"/> : undefined}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                {isLoading ? 'Creating Account...' : 'Create Account'}
                            </Button>

                            <Divider sx={{my: 1.5}}>
                                <Typography variant="body2" color="text.secondary">
                                    OR
                                </Typography>
                            </Divider>

                            <Box sx={{textAlign: 'center'}}>
                                <Typography variant="body2">
                                    Already have an account?{' '}
                                    <Link to="/login"
                                          style={{textDecoration: 'none', color: 'primary.main', fontWeight: 600}}>
                                        Sign In
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={2000}
                    onClose={handleSnackbarClose}
                    message={snackbarMessage}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                />

            </Container>
        </Box>
    );
}

export default Signup;